/**
 * MegaCap playbook workspace (STRATEGY_ROADMAP.md Phase 7, research/megacap/README.md): the register of M tests
 * from the customer's SPX & Mega-Cap playbook, and where each playbook chapter sits. Separate from the 10:00 am
 * programme, whose paper records stay on the SPX desk. Research only: nothing here is a signal or places an order.
 */
import React from "react";
import { Link, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Badge, Icon, PageHead, Panel } from "@/sentinel";
import { QueryBoundary, retryTransient } from "@/lib/queryState";

export const STATUS_LABEL = {
  not_started: "Not started", pre_registered: "Pre-registered", running: "Running", paper_record: "Paper record", closed: "Closed",
};
const WHERE_LABEL = { megacap: "MegaCap test", ten_am: "10:00 am programme", tested: "Tested, no edge" };

const useRegister = () => useQuery({ queryKey: ["megacap"], queryFn: api.megacap, retry: retryTransient, staleTime: 60_000 });

/** Section navigation lives in the workspace rail (and tab bar on mobile); this only frames the page. */
export function MegaCapLayout() {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="megacap-shell">
      <PageHead
        crumb={["MegaCap"]}
        title="MegaCap playbook"
        context="Tests that start from the customer's SPX & Mega-Cap Equity Options playbook (20 Oct 2026). A separate stream from the 10:00 am programme: tests are numbered M1, M2, …; the 10:00 am work keeps the N series and stays on the SPX desk. Research only; Sentinel places no orders."
      />
      <Outlet />
    </div>
  );
}

function StatusBadge({ status }) {
  const tone = status === "running" || status === "paper_record" ? "brand" : "neutral";
  return <Badge tone={tone} data-testid="megacap-status">{STATUS_LABEL[status] || status}</Badge>;
}

export function MegaCapRegister() {
  const q = useRegister();
  return (
    <QueryBoundary query={q} loading={4} what="the MegaCap register" errorTestId="megacap-error" isEmpty={(d) => !d?.tests?.length}>
      {(d) => (
        <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="megacap-register">
          <Panel title="Test register" icon="ListChecks"
            actions={d.doc_url ? (
              <a href={d.doc_url} target="_blank" rel="noreferrer" className="sn-link sn-small" data-testid="megacap-doc-link">
                Customer register doc <Icon name="ExternalLink" size={12} />
              </a>
            ) : null}>
            <div className="sn-table-wrap">
              <table className="sn-table">
                <thead>
                  <tr><th>ID</th><th>Test</th><th>Chapter</th><th>Status</th><th>First check</th><th>Next step</th></tr>
                </thead>
                <tbody>
                  {d.tests.map((t) => (
                    <tr key={t.id} data-testid={`megacap-row-${t.id}`}>
                      <td className="id">{t.id}</td>
                      <td style={{ whiteSpace: "normal" }}>
                        <div className="sn-stack" style={{ gap: 2 }}>
                          <span style={{ color: "var(--text-primary)" }}>{t.name}</span>
                          <span className="sn-small sn-faint">{t.what}</span>
                        </div>
                      </td>
                      <td className="num">{t.chapters.join(", ")}</td>
                      <td><StatusBadge status={t.status} /></td>
                      <td className="sn-small" style={{ whiteSpace: "normal" }}>{t.first_check}</td>
                      <td className="sn-small" style={{ whiteSpace: "normal" }}>{t.next}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="sn-small sn-muted" style={{ margin: "var(--space-3) 0 0" }}>
              Each test is pre-registered before any result is computed, checks its forecast before any profit test, and keeps a paper
              record before any proposal. Updated {d.updated}.
            </p>
          </Panel>
          <Panel title="Overlap with the 10:00 am programme" icon="Link">
            <p className="sn-small" style={{ margin: 0 }}>
              Playbook chapters 1 and 11 (the 0DTE bull put at 10:00 am) are the 10:00 am programme's work. Their paper records
              (the frozen bull put, the ordinary-day records and D0) stay on the SPX desk under <Link className="sn-link" to="/scoring">Scoring</Link>.
              MegaCap links to them and does not count them as its own.
            </p>
          </Panel>
        </div>
      )}
    </QueryBoundary>
  );
}

export function MegaCapChapters() {
  const q = useRegister();
  return (
    <QueryBoundary query={q} loading={6} what="the playbook map" errorTestId="megacap-error" isEmpty={(d) => !d?.chapters?.length}>
      {(d) => (
        <Panel title="Playbook chapters and where they sit" icon="BookOpen" data-testid="megacap-chapters">
          <div className="sn-table-wrap">
            <table className="sn-table">
              <thead><tr><th>Chapter</th><th>Strategy</th><th>Where</th><th>What we found</th></tr></thead>
              <tbody>
                {d.chapters.map((c) => (
                  <tr key={c.chapters} data-where={c.where}>
                    <td className="num">{c.chapters}</td>
                    <td style={{ whiteSpace: "normal" }}>{c.name}</td>
                    <td><Badge tone={c.where === "megacap" ? "brand" : "neutral"}>{WHERE_LABEL[c.where] || c.where}</Badge></td>
                    <td className="sn-small" style={{ whiteSpace: "normal" }}>{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sn-small sn-muted" style={{ margin: "var(--space-3) 0 0" }}>Source: {d.source}.</p>
        </Panel>
      )}
    </QueryBoundary>
  );
}
